import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  BarChart3, TrendingUp, TrendingDown, DollarSign, Target, Users, ArrowUpRight,
  ArrowDownRight, Loader2, RefreshCw, Plus, FileText, Download, Eye, Calendar,
  Sparkles, PieChart, LineChart, Briefcase, Building2, Wallet, Clock, CheckCircle2,
  AlertTriangle, ChevronRight, Layers, Zap, Globe, Activity
} from 'lucide-react';
import { forecastingAPI, campaignsAPI } from '../api';
import { format, subDays, startOfMonth, endOfMonth, startOfQuarter, endOfQuarter } from 'date-fns';
import {
  LineChart as RechartsLine, Line, AreaChart, Area, BarChart, Bar,
  PieChart as RechartsPie, Pie, Cell, ComposedChart,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

const BUDGET_CATEGORIES = [
  { value: 'ad_spend', label: 'Ad Spend', icon: Globe },
  { value: 'creative', label: 'Creative', icon: Sparkles },
  { value: 'tools', label: 'Tools & Software', icon: Zap },
  { value: 'personnel', label: 'Personnel', icon: Users },
  { value: 'events', label: 'Events', icon: Calendar },
  { value: 'other', label: 'Other', icon: Layers },
];

const REPORT_TYPES = [
  { value: 'senior_management', label: 'Senior Management Summary' },
  { value: 'board', label: 'Board Report' },
  { value: 'quarterly_review', label: 'Quarterly Business Review' },
  { value: 'monthly_summary', label: 'Monthly Summary' },
  { value: 'campaign_performance', label: 'Campaign Performance' },
];

export const ExecutiveDashboardPage = () => {
  const { hasRole } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Data state
  const [performance, setPerformance] = useState(null);
  const [budgets, setBudgets] = useState([]);
  const [forecasts, setForecasts] = useState([]);
  const [reports, setReports] = useState([]);
  const [consolidatedBudget, setConsolidatedBudget] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  
  // Dialog state
  const [showCreateForecastDialog, setShowCreateForecastDialog] = useState(false);
  const [showCreateBudgetDialog, setShowCreateBudgetDialog] = useState(false);
  const [showGenerateReportDialog, setShowGenerateReportDialog] = useState(false);
  const [showReportViewer, setShowReportViewer] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [generating, setGenerating] = useState(false);
  
  // Form state
  const [newForecast, setNewForecast] = useState({
    name: '',
    description: '',
    forecast_type: 'campaign',
    channels: [],
    start_date: format(new Date(), 'yyyy-MM-dd'),
    end_date: format(new Date(Date.now() + 90 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'),
    granularity: 'monthly',
    total_budget: 0,
    use_ai: true,
    linked_campaigns: []
  });
  
  const [newBudget, setNewBudget] = useState({
    name: '',
    budget_type: 'marketing',
    fiscal_year: new Date().getFullYear(),
    fiscal_quarter: Math.ceil((new Date().getMonth() + 1) / 3),
    total_planned: 0,
    line_items: []
  });
  
  const [newReport, setNewReport] = useState({
    title: '',
    report_type: 'senior_management',
    period_start: format(startOfMonth(new Date()), 'yyyy-MM-dd'),
    period_end: format(endOfMonth(new Date()), 'yyyy-MM-dd'),
    include_sections: ['marketing', 'sales', 'financial', 'pipeline', 'budget', 'forecast'],
    use_ai: true
  });

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      const [perfRes, budgetRes, forecastRes, reportRes, consolidatedRes, campaignsRes] = await Promise.all([
        forecastingAPI.getPerformanceDashboard().catch(() => ({ data: null })),
        forecastingAPI.getBudgets().catch(() => ({ data: { budgets: [] } })),
        forecastingAPI.getForecasts().catch(() => ({ data: { forecasts: [] } })),
        forecastingAPI.getReports({ limit: 10 }).catch(() => ({ data: { reports: [] } })),
        forecastingAPI.getConsolidatedBudget(new Date().getFullYear()).catch(() => ({ data: null })),
        campaignsAPI.getAll({ limit: 50 }).catch(() => ({ data: [] }))
      ]);
      
      setPerformance(perfRes.data);
      setBudgets(budgetRes.data?.budgets || []);
      setForecasts(forecastRes.data?.forecasts || []);
      setReports(reportRes.data?.reports || []);
      setConsolidatedBudget(consolidatedRes.data);
      setCampaigns(campaignsRes.data?.campaigns || campaignsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateForecast = async () => {
    if (!newForecast.name) {
      toast.error('Forecast name is required');
      return;
    }
    
    try {
      setGenerating(true);
      const res = await forecastingAPI.createForecast(newForecast);
      toast.success('Forecast created successfully');
      setShowCreateForecastDialog(false);
      setNewForecast({
        name: '',
        description: '',
        forecast_type: 'campaign',
        channels: [],
        start_date: format(new Date(), 'yyyy-MM-dd'),
        end_date: format(new Date(Date.now() + 90 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'),
        granularity: 'monthly',
        total_budget: 0,
        use_ai: true,
        linked_campaigns: []
      });
      fetchAllData();
    } catch (error) {
      toast.error('Failed to create forecast');
    } finally {
      setGenerating(false);
    }
  };

  const handleCreateBudget = async () => {
    if (!newBudget.name) {
      toast.error('Budget name is required');
      return;
    }
    
    try {
      await forecastingAPI.createBudget(newBudget);
      toast.success('Budget created successfully');
      setShowCreateBudgetDialog(false);
      setNewBudget({
        name: '',
        budget_type: 'marketing',
        fiscal_year: new Date().getFullYear(),
        fiscal_quarter: Math.ceil((new Date().getMonth() + 1) / 3),
        total_planned: 0,
        line_items: []
      });
      fetchAllData();
    } catch (error) {
      toast.error('Failed to create budget');
    }
  };

  const handleGenerateReport = async () => {
    if (!newReport.title) {
      toast.error('Report title is required');
      return;
    }
    
    try {
      setGenerating(true);
      const res = await forecastingAPI.generateReport(newReport);
      toast.success('Report generated successfully');
      setShowGenerateReportDialog(false);
      setSelectedReport(res.data);
      setShowReportViewer(true);
      fetchAllData();
    } catch (error) {
      toast.error('Failed to generate report');
    } finally {
      setGenerating(false);
    }
  };

  const viewReport = async (report) => {
    try {
      const res = await forecastingAPI.getReport(report.id);
      setSelectedReport(res.data);
      setShowReportViewer(true);
    } catch (error) {
      toast.error('Failed to load report');
    }
  };

  const addBudgetLineItem = () => {
    setNewBudget(prev => ({
      ...prev,
      line_items: [...prev.line_items, {
        category: 'other',
        name: '',
        planned_amount: 0
      }]
    }));
  };

  const updateLineItem = (index, field, value) => {
    setNewBudget(prev => ({
      ...prev,
      line_items: prev.line_items.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const removeLineItem = (index) => {
    setNewBudget(prev => ({
      ...prev,
      line_items: prev.line_items.filter((_, i) => i !== index)
    }));
  };

  // KPI Card Component
  const KPICard = ({ title, value, subtitle, change, changeType, icon: Icon, color }) => (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className={`p-2 rounded-lg ${color || 'bg-blue-100'}`}>
            <Icon className={`h-5 w-5 ${color?.replace('bg-', 'text-').replace('-100', '-600') || 'text-blue-600'}`} />
          </div>
          {change !== undefined && (
            <Badge variant="outline" className={changeType === 'up' ? 'text-green-600' : 'text-red-600'}>
              {changeType === 'up' ? <ArrowUpRight className="h-3 w-3 mr-1" /> : <ArrowDownRight className="h-3 w-3 mr-1" />}
              {change}%
            </Badge>
          )}
        </div>
        <div className="mt-3">
          <p className="text-2xl font-bold">{value}</p>
          <p className="text-sm text-muted-foreground">{title}</p>
          {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const mtd = performance?.month_to_date || {};
  const consolidated = consolidatedBudget?.grand_totals || {};

  return (
    <div className="space-y-6" data-testid="executive-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BarChart3 className="h-6 w-6" />
            Executive Dashboard
          </h1>
          <p className="text-muted-foreground">
            Marketing forecasting, budgets, and executive reporting
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={fetchAllData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button variant="outline" onClick={() => setShowGenerateReportDialog(true)}>
            <FileText className="h-4 w-4 mr-2" />
            Generate Report
          </Button>
          <Button onClick={() => setShowCreateForecastDialog(true)}>
            <Plus className="h-4 w-4 mr-2" />
            New Forecast
          </Button>
        </div>
      </div>

      {/* KPI Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <KPICard
          title="MTD Revenue"
          value={`$${((mtd.marketing_revenue || 0) + (mtd.sales_revenue || 0)).toLocaleString()}`}
          icon={DollarSign}
          color="bg-green-100"
        />
        <KPICard
          title="MTD Leads"
          value={(mtd.leads_generated || 0).toLocaleString()}
          icon={Users}
          color="bg-blue-100"
        />
        <KPICard
          title="Conversions"
          value={(mtd.conversions || 0).toLocaleString()}
          icon={Target}
          color="bg-purple-100"
        />
        <KPICard
          title="Marketing ROI"
          value={`${(mtd.roi || 0).toFixed(1)}x`}
          icon={TrendingUp}
          color="bg-amber-100"
        />
        <KPICard
          title="Budget Used"
          value={`${consolidated.utilization_percent || 0}%`}
          subtitle={`$${(consolidated.total_actual || 0).toLocaleString()} of $${(consolidated.total_planned || 0).toLocaleString()}`}
          icon={Wallet}
          color="bg-cyan-100"
        />
        <KPICard
          title="Deals Won"
          value={(mtd.deals_won || 0).toLocaleString()}
          icon={CheckCircle2}
          color="bg-emerald-100"
        />
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="forecasts">Forecasts</TabsTrigger>
          <TabsTrigger value="budgets">Budgets</TabsTrigger>
          <TabsTrigger value="reports">Reports</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Performance Trend */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Performance Trend</CardTitle>
                <CardDescription>Revenue and leads over time</CardDescription>
              </CardHeader>
              <CardContent>
                {performance?.trend_data?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <ComposedChart data={performance.trend_data}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="snapshot_date" tick={{ fontSize: 11 }} />
                      <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                      <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Legend />
                      <Bar yAxisId="left" dataKey="marketing_revenue" fill="#3B82F6" name="Revenue" />
                      <Line yAxisId="right" type="monotone" dataKey="leads_generated" stroke="#10B981" strokeWidth={2} name="Leads" />
                    </ComposedChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                    No trend data available yet
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Budget Allocation by Type */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Budget by Department</CardTitle>
                <CardDescription>FY {new Date().getFullYear()} allocation</CardDescription>
              </CardHeader>
              <CardContent>
                {consolidatedBudget?.by_type ? (
                  <div className="space-y-4">
                    {Object.entries(consolidatedBudget.by_type).map(([type, data]) => {
                      const utilization = data.total_planned > 0 
                        ? Math.round((data.total_actual / data.total_planned) * 100) 
                        : 0;
                      return (
                        <div key={type} className="space-y-2">
                          <div className="flex items-center justify-between text-sm">
                            <span className="font-medium capitalize">{type}</span>
                            <span className="text-muted-foreground">
                              ${data.total_actual.toLocaleString()} / ${data.total_planned.toLocaleString()}
                            </span>
                          </div>
                          <Progress value={utilization} className="h-2" />
                          <div className="flex justify-between text-xs text-muted-foreground">
                            <span>{utilization}% utilized</span>
                            <span className={data.variance > 0 ? 'text-green-600' : 'text-red-600'}>
                              {data.variance > 0 ? '+' : ''}{data.variance.toLocaleString()} variance
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                    No budget data available
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Active Forecasts & Campaigns */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Active Forecasts</CardTitle>
                  <CardDescription>AI-powered projections</CardDescription>
                </div>
                <Button size="sm" variant="outline" onClick={() => setActiveTab('forecasts')}>
                  View All
                </Button>
              </CardHeader>
              <CardContent>
                {forecasts.filter(f => f.status === 'active').length > 0 ? (
                  <div className="space-y-3">
                    {forecasts.filter(f => f.status === 'active').slice(0, 3).map(forecast => (
                      <div key={forecast.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div>
                          <p className="font-medium flex items-center gap-2">
                            {forecast.name}
                            {forecast.ai_generated && <Sparkles className="h-3 w-3 text-amber-500" />}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {forecast.start_date} to {forecast.end_date}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-medium">${(forecast.total_projected_revenue || 0).toLocaleString()}</p>
                          <p className="text-xs text-muted-foreground">projected revenue</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Activity className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>No active forecasts</p>
                    <Button variant="link" size="sm" onClick={() => setShowCreateForecastDialog(true)}>
                      Create your first forecast
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Recent Reports</CardTitle>
                <CardDescription>Executive summaries</CardDescription>
              </CardHeader>
              <CardContent>
                {reports.length > 0 ? (
                  <div className="space-y-3">
                    {reports.slice(0, 4).map(report => (
                      <div 
                        key={report.id} 
                        className="flex items-center justify-between p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100"
                        onClick={() => viewReport(report)}
                      >
                        <div className="flex items-center gap-3">
                          <FileText className="h-5 w-5 text-blue-500" />
                          <div>
                            <p className="font-medium">{report.title}</p>
                            <p className="text-xs text-muted-foreground">
                              {REPORT_TYPES.find(t => t.value === report.report_type)?.label}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {report.ai_generated && <Sparkles className="h-3 w-3 text-amber-500" />}
                          <Badge variant="outline" className="text-xs">
                            {report.view_count || 0} views
                          </Badge>
                          <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>No reports generated</p>
                    <Button variant="link" size="sm" onClick={() => setShowGenerateReportDialog(true)}>
                      Generate your first report
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Forecasts Tab */}
        <TabsContent value="forecasts" className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Marketing Forecasts</h2>
            <Button onClick={() => setShowCreateForecastDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              New Forecast
            </Button>
          </div>
          
          {forecasts.length > 0 ? (
            <div className="grid gap-4">
              {forecasts.map(forecast => (
                <Card key={forecast.id}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <div className={`p-3 rounded-lg ${
                          forecast.status === 'active' ? 'bg-green-100' : 
                          forecast.status === 'completed' ? 'bg-blue-100' : 'bg-slate-100'
                        }`}>
                          <TrendingUp className={`h-6 w-6 ${
                            forecast.status === 'active' ? 'text-green-600' : 
                            forecast.status === 'completed' ? 'text-blue-600' : 'text-slate-600'
                          }`} />
                        </div>
                        <div>
                          <h3 className="font-semibold flex items-center gap-2">
                            {forecast.name}
                            {forecast.ai_generated && (
                              <Badge variant="outline" className="text-amber-600 bg-amber-50">
                                <Sparkles className="h-3 w-3 mr-1" />
                                AI Generated
                              </Badge>
                            )}
                            <Badge variant={forecast.status === 'active' ? 'default' : 'secondary'}>
                              {forecast.status}
                            </Badge>
                          </h3>
                          <p className="text-sm text-muted-foreground">{forecast.description}</p>
                          <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                            <span>📅 {forecast.start_date} to {forecast.end_date}</span>
                            <span>📊 {forecast.granularity}</span>
                            <span>📈 {forecast.periods?.length || 0} periods</span>
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-green-600">
                          ${(forecast.total_projected_revenue || 0).toLocaleString()}
                        </p>
                        <p className="text-xs text-muted-foreground">projected revenue</p>
                        {forecast.ai_confidence && (
                          <p className="text-xs text-muted-foreground mt-1">
                            {Math.round(forecast.ai_confidence * 100)}% AI confidence
                          </p>
                        )}
                      </div>
                    </div>
                    
                    {/* Scenarios */}
                    {(forecast.scenario_best || forecast.scenario_expected || forecast.scenario_worst) && (
                      <div className="mt-4 pt-4 border-t grid grid-cols-3 gap-4">
                        {forecast.scenario_best && (
                          <div className="text-center p-2 bg-green-50 rounded-lg">
                            <p className="text-xs text-green-600 font-medium">Best Case</p>
                            <p className="font-bold text-green-700">${(forecast.scenario_best.revenue || 0).toLocaleString()}</p>
                          </div>
                        )}
                        {forecast.scenario_expected && (
                          <div className="text-center p-2 bg-blue-50 rounded-lg">
                            <p className="text-xs text-blue-600 font-medium">Expected</p>
                            <p className="font-bold text-blue-700">${(forecast.scenario_expected.revenue || 0).toLocaleString()}</p>
                          </div>
                        )}
                        {forecast.scenario_worst && (
                          <div className="text-center p-2 bg-red-50 rounded-lg">
                            <p className="text-xs text-red-600 font-medium">Worst Case</p>
                            <p className="font-bold text-red-700">${(forecast.scenario_worst.revenue || 0).toLocaleString()}</p>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* AI Insights */}
                    {forecast.ai_insights?.length > 0 && (
                      <div className="mt-4 pt-4 border-t">
                        <p className="text-xs font-medium text-muted-foreground mb-2">AI INSIGHTS</p>
                        <ul className="space-y-1">
                          {forecast.ai_insights.slice(0, 3).map((insight, i) => (
                            <li key={i} className="text-sm flex items-start gap-2">
                              <Sparkles className="h-3 w-3 text-amber-500 mt-1 flex-shrink-0" />
                              {insight}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-16">
                <TrendingUp className="h-16 w-16 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">No forecasts yet</h3>
                <p className="text-muted-foreground mb-4 text-center max-w-md">
                  Create AI-powered forecasts to predict campaign performance and revenue.
                </p>
                <Button onClick={() => setShowCreateForecastDialog(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Forecast
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Budgets Tab */}
        <TabsContent value="budgets" className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Budget Management</h2>
            <Button onClick={() => setShowCreateBudgetDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              New Budget
            </Button>
          </div>

          {/* Consolidated View */}
          {consolidatedBudget && (
            <Card className="bg-gradient-to-r from-slate-50 to-slate-100">
              <CardHeader>
                <CardTitle className="text-lg">Consolidated Budget FY {consolidatedBudget.fiscal_year}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-4 gap-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold">${consolidated.total_planned?.toLocaleString()}</p>
                    <p className="text-sm text-muted-foreground">Total Planned</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold">${consolidated.total_actual?.toLocaleString()}</p>
                    <p className="text-sm text-muted-foreground">Total Actual</p>
                  </div>
                  <div className="text-center">
                    <p className={`text-2xl font-bold ${consolidated.variance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      ${Math.abs(consolidated.variance || 0).toLocaleString()}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {consolidated.variance >= 0 ? 'Under Budget' : 'Over Budget'}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold">{consolidated.utilization_percent}%</p>
                    <p className="text-sm text-muted-foreground">Utilization</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Budget List */}
          {budgets.length > 0 ? (
            <div className="grid gap-4">
              {budgets.map(budget => {
                const utilization = budget.total_planned > 0 
                  ? Math.round((budget.total_actual / budget.total_planned) * 100) 
                  : 0;
                return (
                  <Card key={budget.id}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`p-3 rounded-lg ${
                            budget.budget_type === 'marketing' ? 'bg-blue-100' :
                            budget.budget_type === 'sales' ? 'bg-green-100' :
                            budget.budget_type === 'finance' ? 'bg-purple-100' : 'bg-slate-100'
                          }`}>
                            {budget.budget_type === 'marketing' ? <Briefcase className="h-6 w-6 text-blue-600" /> :
                             budget.budget_type === 'sales' ? <Target className="h-6 w-6 text-green-600" /> :
                             budget.budget_type === 'finance' ? <Building2 className="h-6 w-6 text-purple-600" /> :
                             <Wallet className="h-6 w-6 text-slate-600" />}
                          </div>
                          <div>
                            <h3 className="font-semibold">{budget.name}</h3>
                            <p className="text-sm text-muted-foreground capitalize">
                              {budget.budget_type} • FY{budget.fiscal_year}
                              {budget.fiscal_quarter && ` Q${budget.fiscal_quarter}`}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-8">
                          <div className="w-48">
                            <div className="flex justify-between text-sm mb-1">
                              <span>${budget.total_actual?.toLocaleString()}</span>
                              <span className="text-muted-foreground">${budget.total_planned?.toLocaleString()}</span>
                            </div>
                            <Progress value={utilization} className="h-2" />
                            <p className="text-xs text-muted-foreground mt-1">{utilization}% used</p>
                          </div>
                          <Badge variant={budget.total_variance >= 0 ? 'outline' : 'destructive'}>
                            {budget.total_variance >= 0 ? '+' : ''}{budget.total_variance?.toLocaleString() || 0}
                          </Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-16">
                <Wallet className="h-16 w-16 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">No budgets yet</h3>
                <p className="text-muted-foreground mb-4">Create budgets to track planned vs actual spend.</p>
                <Button onClick={() => setShowCreateBudgetDialog(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Budget
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Reports Tab */}
        <TabsContent value="reports" className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Executive Reports</h2>
            <Button onClick={() => setShowGenerateReportDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Generate Report
            </Button>
          </div>

          {reports.length > 0 ? (
            <div className="grid gap-4">
              {reports.map(report => (
                <Card key={report.id} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => viewReport(report)}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="p-3 rounded-lg bg-blue-100">
                          <FileText className="h-6 w-6 text-blue-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold flex items-center gap-2">
                            {report.title}
                            {report.ai_generated && <Sparkles className="h-4 w-4 text-amber-500" />}
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            {REPORT_TYPES.find(t => t.value === report.report_type)?.label}
                            {' • '}{report.period_start} to {report.period_end}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right text-sm text-muted-foreground">
                          <p>{report.view_count || 0} views</p>
                          <p>{format(new Date(report.generated_at), 'MMM d, yyyy')}</p>
                        </div>
                        <Button variant="ghost" size="icon">
                          <Eye className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-16">
                <FileText className="h-16 w-16 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">No reports yet</h3>
                <p className="text-muted-foreground mb-4">Generate AI-powered executive reports for stakeholders.</p>
                <Button onClick={() => setShowGenerateReportDialog(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Generate Report
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Create Forecast Dialog */}
      <Dialog open={showCreateForecastDialog} onOpenChange={setShowCreateForecastDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create Marketing Forecast</DialogTitle>
            <DialogDescription>
              Build an AI-powered forecast with predictive analytics and scenario planning
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid grid-cols-2 gap-4 max-h-[60vh] overflow-y-auto">
            <div className="col-span-2 space-y-2">
              <Label>Forecast Name</Label>
              <Input
                value={newForecast.name}
                onChange={(e) => setNewForecast({ ...newForecast, name: e.target.value })}
                placeholder="Q1 2026 Marketing Forecast"
              />
            </div>
            
            <div className="col-span-2 space-y-2">
              <Label>Description</Label>
              <Textarea
                value={newForecast.description}
                onChange={(e) => setNewForecast({ ...newForecast, description: e.target.value })}
                placeholder="Describe the forecast objectives..."
                rows={2}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Start Date</Label>
              <Input
                type="date"
                value={newForecast.start_date}
                onChange={(e) => setNewForecast({ ...newForecast, start_date: e.target.value })}
              />
            </div>
            
            <div className="space-y-2">
              <Label>End Date</Label>
              <Input
                type="date"
                value={newForecast.end_date}
                onChange={(e) => setNewForecast({ ...newForecast, end_date: e.target.value })}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Granularity</Label>
              <Select
                value={newForecast.granularity}
                onValueChange={(v) => setNewForecast({ ...newForecast, granularity: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Total Budget</Label>
              <Input
                type="number"
                value={newForecast.total_budget}
                onChange={(e) => setNewForecast({ ...newForecast, total_budget: parseFloat(e.target.value) || 0 })}
                placeholder="50000"
              />
            </div>
            
            <div className="col-span-2 flex items-center gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
              <Sparkles className="h-5 w-5 text-amber-600" />
              <div className="flex-1">
                <p className="font-medium text-amber-800">AI-Powered Forecast</p>
                <p className="text-sm text-amber-700">Generate predictions with seasonality analysis and scenario planning</p>
              </div>
              <input
                type="checkbox"
                checked={newForecast.use_ai}
                onChange={(e) => setNewForecast({ ...newForecast, use_ai: e.target.checked })}
                className="h-4 w-4"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateForecastDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateForecast} disabled={generating}>
              {generating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
              {generating ? 'Generating...' : 'Create Forecast'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Budget Dialog */}
      <Dialog open={showCreateBudgetDialog} onOpenChange={setShowCreateBudgetDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create Budget</DialogTitle>
            <DialogDescription>
              Set up a budget with line items for tracking
            </DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="max-h-[60vh]">
            <div className="space-y-4 pr-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Budget Name</Label>
                  <Input
                    value={newBudget.name}
                    onChange={(e) => setNewBudget({ ...newBudget, name: e.target.value })}
                    placeholder="Q1 Marketing Budget"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Budget Type</Label>
                  <Select
                    value={newBudget.budget_type}
                    onValueChange={(v) => setNewBudget({ ...newBudget, budget_type: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="marketing">Marketing</SelectItem>
                      <SelectItem value="sales">Sales</SelectItem>
                      <SelectItem value="finance">Finance</SelectItem>
                      <SelectItem value="operations">Operations</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Fiscal Year</Label>
                  <Input
                    type="number"
                    value={newBudget.fiscal_year}
                    onChange={(e) => setNewBudget({ ...newBudget, fiscal_year: parseInt(e.target.value) })}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Total Planned</Label>
                  <Input
                    type="number"
                    value={newBudget.total_planned}
                    onChange={(e) => setNewBudget({ ...newBudget, total_planned: parseFloat(e.target.value) || 0 })}
                    placeholder="100000"
                  />
                </div>
              </div>

              {/* Line Items */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Line Items</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addBudgetLineItem}>
                    <Plus className="h-3 w-3 mr-1" />
                    Add Item
                  </Button>
                </div>
                
                {newBudget.line_items.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2 p-2 border rounded-lg">
                    <Select
                      value={item.category}
                      onValueChange={(v) => updateLineItem(idx, 'category', v)}
                    >
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {BUDGET_CATEGORIES.map(cat => (
                          <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      value={item.name}
                      onChange={(e) => updateLineItem(idx, 'name', e.target.value)}
                      placeholder="Item name"
                      className="flex-1"
                    />
                    <Input
                      type="number"
                      value={item.planned_amount}
                      onChange={(e) => updateLineItem(idx, 'planned_amount', parseFloat(e.target.value) || 0)}
                      placeholder="Amount"
                      className="w-28"
                    />
                    <Button variant="ghost" size="icon" onClick={() => removeLineItem(idx)}>
                      <AlertTriangle className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </ScrollArea>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateBudgetDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateBudget}>Create Budget</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Generate Report Dialog */}
      <Dialog open={showGenerateReportDialog} onOpenChange={setShowGenerateReportDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Generate Executive Report</DialogTitle>
            <DialogDescription>
              Create an AI-powered report for management or board meetings
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Report Title</Label>
              <Input
                value={newReport.title}
                onChange={(e) => setNewReport({ ...newReport, title: e.target.value })}
                placeholder="January 2026 Executive Summary"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Report Type</Label>
              <Select
                value={newReport.report_type}
                onValueChange={(v) => setNewReport({ ...newReport, report_type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {REPORT_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Period Start</Label>
                <Input
                  type="date"
                  value={newReport.period_start}
                  onChange={(e) => setNewReport({ ...newReport, period_start: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Period End</Label>
                <Input
                  type="date"
                  value={newReport.period_end}
                  onChange={(e) => setNewReport({ ...newReport, period_end: e.target.value })}
                />
              </div>
            </div>
            
            <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
              <Sparkles className="h-5 w-5 text-amber-600" />
              <div className="flex-1">
                <p className="font-medium text-amber-800">AI-Generated Content</p>
                <p className="text-sm text-amber-700">Include executive summary, insights, and recommendations</p>
              </div>
              <input
                type="checkbox"
                checked={newReport.use_ai}
                onChange={(e) => setNewReport({ ...newReport, use_ai: e.target.checked })}
                className="h-4 w-4"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGenerateReportDialog(false)}>Cancel</Button>
            <Button onClick={handleGenerateReport} disabled={generating}>
              {generating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileText className="h-4 w-4 mr-2" />}
              {generating ? 'Generating...' : 'Generate Report'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Report Viewer Dialog */}
      <Dialog open={showReportViewer} onOpenChange={setShowReportViewer}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedReport?.title}
              {selectedReport?.ai_generated && <Sparkles className="h-4 w-4 text-amber-500" />}
            </DialogTitle>
            <DialogDescription>
              {REPORT_TYPES.find(t => t.value === selectedReport?.report_type)?.label}
              {' • '}{selectedReport?.period_start} to {selectedReport?.period_end}
            </DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="max-h-[70vh]">
            {selectedReport && (
              <div className="space-y-6 pr-4">
                {/* Executive Summary */}
                {selectedReport.executive_summary && (
                  <div>
                    <h3 className="font-semibold text-lg mb-2">Executive Summary</h3>
                    <p className="text-muted-foreground whitespace-pre-wrap">{selectedReport.executive_summary}</p>
                  </div>
                )}

                {/* Key Metrics */}
                {Object.keys(selectedReport.key_metrics || {}).length > 0 && (
                  <div>
                    <h3 className="font-semibold text-lg mb-2">Key Metrics</h3>
                    <div className="grid grid-cols-3 gap-4">
                      {Object.entries(selectedReport.key_metrics).map(([key, value]) => (
                        <div key={key} className="p-3 bg-slate-50 rounded-lg">
                          <p className="text-sm text-muted-foreground capitalize">{key.replace(/_/g, ' ')}</p>
                          <p className="text-xl font-bold">
                            {typeof value === 'number' ? 
                              (key.includes('revenue') || key.includes('value') ? `$${value.toLocaleString()}` : 
                               key.includes('rate') || key.includes('percent') ? `${value}%` : value.toLocaleString())
                              : value}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Highlights */}
                {selectedReport.highlights?.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-lg mb-2 text-green-700">Highlights</h3>
                    <ul className="space-y-2">
                      {selectedReport.highlights.map((item, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Challenges */}
                {selectedReport.challenges?.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-lg mb-2 text-amber-700">Challenges</h3>
                    <ul className="space-y-2">
                      {selectedReport.challenges.map((item, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Recommendations */}
                {selectedReport.recommendations?.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-lg mb-2 text-blue-700">Recommendations</h3>
                    <ul className="space-y-2">
                      {selectedReport.recommendations.map((item, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <Target className="h-4 w-4 text-blue-500 mt-0.5" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* AI Insights */}
                {selectedReport.ai_insights?.length > 0 && (
                  <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                    <h3 className="font-semibold text-lg mb-2 flex items-center gap-2">
                      <Sparkles className="h-5 w-5 text-amber-500" />
                      AI Insights
                    </h3>
                    <ul className="space-y-2">
                      {selectedReport.ai_insights.map((item, i) => (
                        <li key={i} className="text-amber-800">{item}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </ScrollArea>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReportViewer(false)}>Close</Button>
            <Button variant="outline">
              <Download className="h-4 w-4 mr-2" />
              Export PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExecutiveDashboardPage;
